import { useState, useEffect, useCallback } from "react";
import apiService from "../services/api";

// offline data
import events from "../data/events";
import sliders from "../data/sliders";

// utils
import { mapEventsToActivities } from "../utils/mapEventsToActivities";

/**
 * Generic fetch hook
 */
export const useFetch = (fetchFunction, fallbackData) => {
  const [data, setData] = useState(fallbackData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useApi, setUseApi] = useState(true);

  const fetchData = useCallback(async () => {
    // Kalau sudah diputus pakai fallback
    if (!useApi) {
      setData(fallbackData);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // offline detection
      if (!navigator.onLine) {
        throw new Error("Offline mode");
      }

      console.log("🔄 Trying to fetch from API...");
      const result = await fetchFunction();

      // support API: { data: [...] } atau langsung array
      const resolvedData = result?.data ?? result;

      console.log("✅ API Success");
      setData(resolvedData);
    } catch (err) {
      console.warn("⚠️ API Error, using fallback data:", err.message);
      setError(err.message);
      setData(fallbackData);
      setUseApi(false); // stop retry API otomatis
    } finally {
      setLoading(false);
    }
  }, [useApi]);

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    usingFallback: !useApi || !!error,
  };
};

/**
 * =============================
 * SPECIFIC HOOKS
 * =============================
 */

/**
 * Activities for Home (mapped from events)
 */
export const useActivities = (customFallback = null) => {
  const defaultFallback = mapEventsToActivities(events);
  const fallbackActivities = customFallback ?? defaultFallback;

  return useFetch(
    async () => {
      const res = await apiService.getEvents();
      return mapEventsToActivities(res.data ?? res);
    },
    fallbackActivities
  );
};

/**
 * Sliders
 */
export const useSliders = (customFallback = null) => {
  const defaultFallback = customFallback ?? sliders;
  return useFetch(
    () => apiService.getSliders(),
    defaultFallback
  );
};

/**
 * Links
 */
export const useLinks = (fallbackData = []) => {
  return useFetch(() => apiService.getLinks(), fallbackData);
};

/**
 * Static content (About, etc)
 */
export const useContent = (fallbackData = {}) => {
  return useFetch(() => apiService.getContent(), fallbackData);
};
